import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { UserModel } from '../../../models/user.model';
import { InitialsPipe } from '../../../pipes/initials.pipe';
import * as userActions from '../../../state/actions/user.actions';
import { AppState } from '../../../state/appstate';
import { BtnSubmitComponent } from '../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../components/dialogs/dlg-confirm/dlg-confirm.component';

@Component({
  selector: 'app-dlg-profile-update',
  standalone: true,
  imports: [BtnSubmitComponent, ReactiveFormsModule, InitialsPipe],
  templateUrl: './dlg-profile-update.component.html',
  styleUrl: './dlg-profile-update.component.css',
})
export class DlgProfileUpdateComponent implements OnInit, OnDestroy {
  store: Store<AppState> = inject(Store<AppState>);
  readonly dialogRef = inject(MatDialogRef<DlgProfileUpdateComponent>);
  private dialog: MatDialog = inject(MatDialog);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private actions$: Actions = inject(Actions);
  fb: FormBuilder = inject(FormBuilder);

  title: string = 'Update Profile';
  description: string = `Group Membership requires sharing your name with Group administrators`;
  action: string = 'Update';

  user: UserModel | null = null;

  form: FormGroup;
  working: boolean = false;

  exit$: Subject<void> = new Subject();

  constructor() {
    this.form = this.fb.group({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(150),
      ]),
    });
  }

  ngOnInit(): void {
    document.body.style.overflow = 'hidden';
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
        this.form.patchValue({ name: user?.name });
      });

    // Subscribe to loadUserSuccess action
    this.actions$
      .pipe(ofType(userActions.loadUserSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        // console.log('Profile loaded successfully');
        this.working = false;
        this.dialogRef.close(true);
      });

    // Subscribe to updateFailure action (optional, for error handling)
    this.actions$
      .pipe(ofType(userActions.updateFailure), takeUntil(this.exit$))
      .subscribe(({ error }) => {
        // console.log('Profile update failed');
        this.working = false;
        this.onShowAlert(error);
      });
  }

  ngOnDestroy(): void {
    document.body.style.overflow = 'auto';
    this.exit$.next();
    this.exit$.complete();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  submit() {
    // console.log('Form submitted:', this.form.value);
    this.working = true;

    if (this.form.invalid || this.form.value.name === '') {
      this.working = false;
      return;
    }

    // console.log('Form valid');
    this.store.dispatch(
      userActions.update({
        user: { ...this.user, name: this.form.value.name },
      }),
    );
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
